import { type CategoryEntry } from "./TechnicalSpecificationsCategoryList.types";

/** Maximum number of options displayed in preview mode (showAll = false). */
const MAX_PREVIEW_OPTIONS = 3;

/**
 * Returns the category entries to display based on the `showAll` state.
 *
 * Preview mode (`showAll = false`) enforces two business rules:
 * - **Max options**: at most `MAX_PREVIEW_OPTIONS` options are shown (triggers show-more when total > `MAX_PREVIEW_OPTIONS`).
 * - **Multi-category**: when 2+ categories exist, only the first is shown to avoid a partially-visible second category (triggers show-more regardless of option count).
 *
 * @param categoryEntries - Ordered list of `[categoryId, options]` tuples to filter.
 * @param showAll - When `true`, returns all entries unfiltered.
 * @returns Filtered `[categoryId, options]` tuples for the current display state.
 */
export const computeVisibleCategories = (
  categoryEntries: CategoryEntry[],
  showAll: boolean
): CategoryEntry[] => {
  if (showAll) {
    return categoryEntries;
  }

  let remainingSlots = MAX_PREVIEW_OPTIONS;
  const visibleEntries: CategoryEntry[] = [];

  for (const [categoryId, categoryOptions] of categoryEntries) {
    if (categoryEntries.length >= 2 && visibleEntries.length > 0) {
      break;
    }

    if (remainingSlots <= 0) {
      break;
    }

    const slicedOptions = categoryOptions.slice(0, remainingSlots);
    remainingSlots -= slicedOptions.length;
    visibleEntries.push([categoryId, slicedOptions]);
  }

  return visibleEntries;
};
